import * as z from 'zod/mini';
import {
  DISPLAY_NAME_MAX_LENGTH,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PHONE_MAX_LENGTH,
  PROFILE_TITLE_MAX_LENGTH,
  USERNAME_MAX_LENGTH,
} from '../constants.ts';
import { idSchema, timestampSchema } from './common.ts';

// Accounts. The owner is the one account that cannot be deleted; admins
// manage admins and users; users create and edit but never delete a client
// or a property; a client account is a customer's own sign-in to the
// portal, limited to one client, reading it and raising service requests
// (shared/roles.ts holds the rules).

export const ROLES = ['owner', 'admin', 'user', 'client'] as const;
export type Role = (typeof ROLES)[number];
export const roleSchema = z.enum(ROLES);
export const ROLE_LABELS: Readonly<Record<Role, string>> = {
  owner: 'Owner',
  admin: 'Admin',
  user: 'User',
  client: 'Client',
};
/** The roles an admin or the owner may hand out. */
export const ASSIGNABLE_ROLES = ['admin', 'user', 'client'] as const;
export const assignableRoleSchema = z.enum(ASSIGNABLE_ROLES);

export const usernameSchema = z
  .string()
  .check(
    z.minLength(1),
    z.maxLength(USERNAME_MAX_LENGTH),
    z.regex(/^[\w.-]+$/u, 'Letters, digits, dots, dashes, and underscores only'),
  );
export const passwordSchema = z
  .string()
  .check(
    z.minLength(PASSWORD_MIN_LENGTH, `At least ${String(PASSWORD_MIN_LENGTH)} characters`),
    z.maxLength(PASSWORD_MAX_LENGTH),
  );
const currentPasswordSchema = z.string().check(z.minLength(1), z.maxLength(PASSWORD_MAX_LENGTH));
export const emailSchema = z.union([z.literal(''), z.email('Not an email address')]);
/** The name shown beside the username: free text, blank means "use the username". */
export const displayNameSchema = z.string().check(z.trim(), z.maxLength(DISPLAY_NAME_MAX_LENGTH));
export const profileTitleSchema = z.string().check(z.trim(), z.maxLength(PROFILE_TITLE_MAX_LENGTH));
export const phoneSchema = z.string().check(z.trim(), z.maxLength(PHONE_MAX_LENGTH));

export const userSchema = z.object({
  id: idSchema,
  username: z.string(),
  email: z.string(),
  role: roleSchema,
  /** Someone else set the password; the account must pick its own first. */
  mustChangePassword: z._default(z.boolean(), false),
  /** An authenticator app is enrolled and required at sign-in. */
  mfaEnabled: z._default(z.boolean(), false),
  displayName: z._default(z.string(), ''),
  title: z._default(z.string(), ''),
  phone: z._default(z.string(), ''),
  /** When the picture last changed, or null without one; the app keys its cache on it. */
  avatarUpdatedAt: z._default(z.nullable(z.string()), null),
  /**
   * The one client this account is limited to (ROADMAP M1, K7), or null
   * for an unscoped account. Set by a manager on the accounts page; read by
   * the Worker's scope middleware on every request.
   */
  clientScope: z._default(z.nullable(idSchema), null),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type User = z.infer<typeof userSchema>;

export const userInputSchema = z.strictObject({
  username: usernameSchema,
  email: emailSchema,
  role: assignableRoleSchema,
  password: passwordSchema,
  /** The first password is a handover; by default the person replaces it at first sign-in. */
  mustChangePassword: z._default(z.boolean(), true),
  /** The one client to limit the account to (ROADMAP M1); a client account must have one (M2). */
  clientScope: z._default(z.nullable(idSchema), null),
});
export type UserInput = z.infer<typeof userInputSchema>;

/** What anyone may change about their own account: the profile. */
export const profilePatchSchema = z.strictObject({
  email: z.optional(emailSchema),
  displayName: z.optional(displayNameSchema),
  title: z.optional(profileTitleSchema),
  phone: z.optional(phoneSchema),
});
export type ProfilePatch = z.infer<typeof profilePatchSchema>;

/** The name to show for an account: the display name, or the username. */
export function shownName(user: {
  readonly username: string;
  readonly displayName: string;
}): string {
  return user.displayName === '' ? user.username : user.displayName;
}

/** Where an account's picture is served from, keyed so a new picture shows at once. */
export function avatarHref(user: {
  readonly id: string;
  readonly avatarUpdatedAt: string | null;
}): string | null {
  return user.avatarUpdatedAt === null
    ? null
    : `/api/users/${user.id}/avatar?v=${encodeURIComponent(user.avatarUpdatedAt)}`;
}

export const userPatchSchema = z.strictObject({
  email: z.optional(emailSchema),
  role: z.optional(assignableRoleSchema),
  /** A client id limits the account to that client; null lifts the limit. */
  clientScope: z.optional(z.nullable(idSchema)),
});
export type UserPatch = z.infer<typeof userPatchSchema>;

/** Setting a password: one's own needs the current one; an admin's for a user does not. */
export const passwordChangeSchema = z.strictObject({
  currentPassword: currentPasswordSchema,
  newPassword: passwordSchema,
});
export type PasswordChange = z.infer<typeof passwordChangeSchema>;
export const passwordSetSchema = z.strictObject({ password: passwordSchema });
export type PasswordSet = z.infer<typeof passwordSetSchema>;

export const resetRequestSchema = z.strictObject({
  username: z.string().check(z.minLength(1), z.maxLength(USERNAME_MAX_LENGTH)),
});
export type ResetRequest = z.infer<typeof resetRequestSchema>;
export const resetSchema = z.strictObject({
  token: z.string().check(z.minLength(1), z.maxLength(PASSWORD_MAX_LENGTH)),
  password: passwordSchema,
});
export type ResetInput = z.infer<typeof resetSchema>;

export const transferSchema = z.strictObject({ userId: idSchema });
export type Transfer = z.infer<typeof transferSchema>;

export const userListSchema = z.object({ users: z.array(userSchema) });
export const userEnvelopeSchema = z.object({ user: userSchema });

/**
 * What /api/auth/session says: who is signed in, whether reset mail works,
 * and whether device credentials can be stored (CREDENTIAL_KEY is set).
 */
export const sessionStatusSchema = z.object({
  authenticated: z.boolean(),
  userId: z.optional(idSchema),
  username: z.optional(z.string()),
  role: z.optional(roleSchema),
  /** Null is explicitly unscoped; an older response without this field is unknown. */
  clientScope: z.optional(z.nullable(idSchema)),
  email: z.optional(z.string()),
  mail: z.boolean(),
  credentials: z._default(z.boolean(), false),
  /** The account must set its own password before it can do anything else. */
  passwordChangeRequired: z._default(z.boolean(), false),
  /** The signed-in account has a second factor on. */
  mfaEnabled: z._default(z.boolean(), false),
  /** The signed-in account's display name and picture stamp, for the shell. */
  displayName: z._default(z.string(), ''),
  avatarUpdatedAt: z._default(z.nullable(z.string()), null),
  /** Addresses can be looked up and mapped (MAPBOX_TOKEN is set). */
  addressLookup: z._default(z.boolean(), false),
});
export type SessionStatus = z.infer<typeof sessionStatusSchema>;

/** One signed-in device of an account, from GET /api/users/me/sessions. */
export const sessionEntrySchema = z.object({
  id: idSchema,
  createdAt: z.string(),
  lastSeenAt: z.string(),
  expiresAt: z.string(),
  userAgent: z.string(),
  /** The browser and device the user agent describes ("Chrome on Android"). */
  device: z.string(),
  ip: z.string(),
  /** The session making the request. */
  current: z.boolean(),
});
export type SessionEntry = z.infer<typeof sessionEntrySchema>;
